import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Address, AddressType } from 'src/app/shared-core/models/checkout-data.model';
import { TextValue } from 'src/app/shared-core/models/text-value.model';
import { CheckoutService } from 'src/app/shared-core/services/checkout.service';
import { hasError, VistekValidation } from 'src/app/shared-core/services/validation.service';
import { finalize, first, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-address-edit',
  templateUrl: './address-edit.component.html',
  styleUrls: ['./address-edit.component.scss'],
})
export class AddressEditComponent implements OnInit, OnDestroy, OnChanges {
  googleOptions: { componentRestrictions: { country: string[] } };
  isProvincesLoading = false;
  provinceOptions: TextValue[];
  countryOptions: TextValue[] = [];
  isShippingAddress: boolean;
  addressForm: FormGroup;
  addressForm$: Subscription;

  @Input() public address: Address;
  @Input() public scope: string;
  
  constructor(
      private service: CheckoutService, 
      private formBuilder: FormBuilder
  ) {}

  public ngOnDestroy(): void {
    this.addressForm$?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.address && !changes.address.isFirstChange()) {
      this.ngOnInit();
    }
  }

  public ngOnInit(): void {
    this.countryOptions = [
      { Text: 'Canada', Value: '1' }
    ];
      
    this.googleOptions = {
      componentRestrictions: { country: ['CA'] },
    };
      
    if (this.scope === 'billing') {
      this.countryOptions.push({ Text: 'United States', Value: '2' });
      this.googleOptions.componentRestrictions.country.push('US');
    }
      
    if (!this.address) {
        return;
    }
    
    this.isShippingAddress = this.address.AddressType == AddressType.Shipping;
    if (this.address.CountryID) {
      this.loadProvinces(this.address.CountryID).subscribe();
    }

    let formObj = {
      address: [this.address.Address_, [Validators.required]],
      address2: [this.address.Address2],
      country: [this.address.CountryID?.toString(), [Validators.required, Validators.minLength(1)]],
      province: [this.address.StateProvinceID?.toString(), [Validators.required]],
      city: [this.address.City, [Validators.required]],
      postalCode: [this.address.PostalCodeZip, [Validators.required, VistekValidation.postalCodeZip('country')]],
      firstName: [this.address.FirstName, [Validators.required, Validators.minLength(2), Validators.pattern(/^(?=.*\S)[a-zA-ZÀ-ÿ\s\-\'.]+$/)]],
      lastName: [this.address.LastName, [Validators.required, Validators.minLength(2), Validators.pattern(/^(?=.*\S)[a-zA-ZÀ-ÿ\s\-\'.]+$/)]],
      company: [this.address.Company],
      phone: [this.address.Phone, [Validators.required]],
    };

    this.addressForm = this.formBuilder.group(formObj);
    
    this.addressForm$ = this.addressForm.valueChanges.subscribe((form) => {
      if (!this.addressForm.valid) {
        return;
      }

      const addressPatch: Partial<Address> = {
        Address2: form.address2,
        Address_: form.address,
        City: form.city,
        Company: form.company,
        CountryID: form.country,
        CountryName: this.countryOptions.find((el) => el.Value == form.country).Text,
        FirstName: form.firstName,
        LastName: form.lastName,
        Phone: form.phone,
        PostalCodeZip: form.postalCode,
        StateProvinceID: form.province,
        StateProvinceName: this.provinceOptions.find((el) => el.Value == form.province).Text,
      };

      this.address = { ...this.address, ...addressPatch };
    });
  }

  public isValid(): boolean {
    this.addressForm.markAllAsTouched();
    this.addressForm.updateValueAndValidity();
    return this.addressForm.valid;
  }

  public getAddress(): Address {
    const address = this.addressForm.value;

    const mapped = {
      AddressID: this.address.AddressID,
      AddressType: this.isShippingAddress ? AddressType.Shipping : AddressType.Billing,
      Description: this.address.Description,
      FirstName: address.firstName,
      LastName: address.lastName,
      FullName: address.firstName + ' ' + address.lastName,
      Email: this.address.Email,
      Company: address.company,
      CountryID: +address.country,
      CountryName: this.countryOptions.find((el) => el.Value == address.country).Text,
      Address_: address.address,
      Address2: address.address2,
      City: address.city,
      StateProvinceID: +address.province,
      StateProvinceName: this.provinceOptions.find((el) => el.Value == address.province).Text,
      PostalCodeZip: address.postalCode,
      Phone: this.address.Phone,
    } as Address;

    return mapped;
}

  hasError = (formControlName: string, error: string | string[] = null): boolean => {
    return hasError(this.addressForm, formControlName, error);
  };

  loadProvinces(value: number) {      
    this.isProvincesLoading = true;
    return this.service.GetProvinces(value).pipe(
      first(),
      finalize(() => {
        this.isProvincesLoading = false;
      }),
      tap((list) => {
        this.provinceOptions = list;
      })
    );
  }

  onCountryChange(value: number): void {
    this.addressForm.patchValue({
      province: null,
      postalCode: null,
    });
    this.loadProvinces(value).subscribe();
  }

  get valid(): boolean {
    return this.addressForm.valid;
    
  }
  addressChanged(place: any) {
    for (let ctrl in this.addressForm.controls) {
      switch (ctrl) {
        case 'address': {
          const subpremise = place.address_components.find((i) => i.types.includes('subpremise'))?.long_name;
          const street_number = place.address_components.find((i) => i.types.includes('street_number'))?.long_name;
          const route = place.address_components.find((i) => i.types.includes('route'))?.long_name;
          this.addressForm.patchValue({ [ctrl]: [street_number, route].join(' ') });

          if (subpremise) {
            this.addressForm.patchValue({ address2: subpremise });
          }
          break;
        }
        case 'country': {
          const country = place.address_components.find((i) => i.types.includes('country')).long_name;
          const countryId = this.countryOptions.find((i) => i.Text == country)?.Value;
          this.addressForm.patchValue({ [ctrl]: countryId });
          break;
        }
        case 'province': {
          const country = place.address_components.find((i) => i.types.includes('country')).long_name;
          const countryId = this.countryOptions.find((i) => i.Text == country)?.Value;
          this.loadProvinces(+countryId)
            .pipe(first())
            .subscribe((list) => {
              let province = place.address_components.find((i) =>
                i.types.includes('administrative_area_level_1')
              ).long_name;
              if (province == 'Québec') {
                // temp fix until db is updated
                province = 'Quebec';
              }

              const provinceId = list.find((i) => i.Text == province)?.Value;
              this.addressForm.patchValue({ [ctrl]: provinceId });
            });
          break;
        }
        case 'city': {
          let value = place.address_components.find(
            (i) => i.types.includes('locality') || i.types.includes('administrative_area_level_3')
          )?.long_name;
          this.addressForm.patchValue({ [ctrl]: value });
          break;
        }
        case 'postalCode': {
          let value = place.address_components.find((i) => i.types.includes('postal_code'))?.long_name;
          this.addressForm.patchValue({ [ctrl]: value });
          break;
        }
      }
    }
  }
}
